/**
 * Facility rounds: who the provider has seen on today's round at a building (Isaiah Cotto, 2026-10-27).
 *
 * Mirrors backend/src/routes/emergeDaySheetRounds.js and services/emerge/providerRounds.js;
 * docs/provider/FACILITY_ROUNDS.md. Seen is stored by the server per (building, visit day, resident), with who and
 * when; the visit day is the server's (today in America/New_York) and is never sent from here.
 */
import { api } from "../../../services/api";

/** The services/api contract (as twlPipelineApi.ts): `status` absent with no `code` means the server was never reached. */
interface ApiResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
  message?: string;
  status?: number;
}

export type SeenSource = "MANUAL" | "TWL_Q";

export interface SeenBy {
  first_name: string;
  /** The viewer marked it. */
  mine: boolean;
}

export interface RoundOutcome {
  key: string;
  /** The chip's own words: "TWL Q", "TWL · DNQ", ... */
  label: string;
  lead?: boolean;
}

export interface RoundResident {
  id: string;
  /** Index in the WHOLE worklist under the walk order (the page a resident is on = floor(position / limit)). */
  position: number;
  seen: boolean;
  source: SeenSource | null;
  by: SeenBy | null;
  /** timestamptz, ISO. */
  at: string | null;
  /** Today's TWL Q outcome, read from today's review line; null when none is recorded. */
  outcome: RoundOutcome | null;
  /** Flagged as a priority at this building (not per day; stays until cleared). Absent before 20261027d. */
  priority?: RoundPriority | null;
  /** Put back on the Current manifest (20261027i): who and why. Absent before 20261027i. */
  added_back?: RoundAddedBack | null;
}

export interface RoundAddedBack {
  reason: string | null;
  kind: "PROVIDER" | "OFFICE";
  by: SeenBy;
  at: string | null;
}

/** Earlier days' rounds (FR-7): read-only. */
export interface RoundHistory {
  today: string;
  days: { visit_date: string; seen: number; today: boolean }[];
  visit_date: string | null;
  read_only?: boolean;
  residents: {
    id: string; first_name: string | null; last_name: string | null; room: string | null; seen_at: string | null;
    source: SeenSource; by: SeenBy; outcome: RoundOutcome | null;
  }[];
  counts: { seen: number; by_outcome: { key: string; label: string; count: number }[]; no_outcome: number } | null;
  not_yours: number;
}

export interface RoundPriority {
  /** Optional, at most 100 characters. */
  reason: string | null;
  /** Who set it: a provider on the rounds table, or the office (a VA / PA manager) from Manifest Review. */
  kind?: "PROVIDER" | "OFFICE";
  by: SeenBy;
  at: string | null;
}

export interface PriorityResult {
  patient_id: string;
  changed: boolean;
  priority: RoundPriority | null;
}

export const PRIORITY_REASON_MAX = 100;

export interface RoundProgress {
  total: number;
  seen: number;
  remaining: number;
  last_seen_at: string | null;
  complete: boolean;
}

export interface RoundTally {
  seen: number;
  by_outcome: { key: string; label: string; count: number }[];
  no_outcome: number;
}

export interface Round {
  visit_date: string;
  lens: string;
  order: string;
  /** The server would accept a mark here from this caller. */
  can_mark: boolean;
  /** Why nothing can be marked here (a building closed for providers), in words; null otherwise. */
  closed: string | null;
  worklist_total: number;
  /** Residents of this worklist the caller cannot act on: a count only. */
  not_yours: number;
  /** The worklist is longer than one round reads (the server's 500): the counts cover the first 500. */
  capped?: boolean;
  /** Priority marks are switched on (20261027d applied). Absent on an older server. */
  priority_ready?: boolean;
  /** "Add back" is switched on (20261027i applied). */
  add_back_ready?: boolean;
  priority_count?: number;
  residents: RoundResident[];
  progress: RoundProgress;
  tally: RoundTally;
}

export interface SeenResult {
  patient_id: string;
  visit_date: string;
  /** False: it already was (a double tap, a retry, another provider got there first). */
  changed: boolean;
  seen: boolean;
  source: SeenSource | null;
  by: SeenBy | null;
  at: string | null;
}

/** A payload is a round only when it has the round's shape; anything else (an older server, a proxy page) is "off". */
export function isRound(x: unknown): x is Round {
  const r = x as Round | null;
  return Boolean(
    r && typeof r === "object" && Array.isArray(r.residents) && r.progress && typeof r.progress.total === "number"
      && typeof r.can_mark === "boolean",
  );
}

export const roundsApi = {
  get: async (facilityId: string, lens: string, order: string) =>
    (await api.get(
      `/emerge/day-sheet/${facilityId}/rounds?lens=${encodeURIComponent(lens)}&order=${encodeURIComponent(order)}`,
    )) as ApiResult<Round>,
  mark: async (facilityId: string, patientId: string, seen: boolean) =>
    (await api.post(`/emerge/day-sheet/${facilityId}/rounds/${patientId}`, { seen })) as ApiResult<SeenResult>,
  addBack: async (facilityId: string, patientId: string, add: boolean, reason?: string | null) =>
    (await api.post(
      `/emerge/day-sheet/${facilityId}/rounds/${patientId}/add-back`,
      add ? { add, reason: reason && reason.trim() ? reason.trim() : null } : { add },
    )) as ApiResult<{ patient_id: string; changed: boolean; added_back: RoundAddedBack | null }>,
  history: async (facilityId: string, day?: string | null) =>
    (await api.get(`/emerge/day-sheet/${facilityId}/rounds/history${day ? `?day=${encodeURIComponent(day)}` : ""}`)) as ApiResult<RoundHistory>,
  priority: async (facilityId: string, patientId: string, priority: boolean, reason?: string | null) =>
    (await api.post(
      `/emerge/day-sheet/${facilityId}/rounds/${patientId}/priority`,
      priority ? { priority, reason: reason && reason.trim() ? reason.trim() : null } : { priority },
    )) as ApiResult<PriorityResult>,
};
